import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, RefreshCw, Search, Truck, X } from 'lucide-react';
import {
  createOrGetDeliveryConversation,
  createOrGetSupportConversation,
  fetchAdminDriverTimeline,
} from './api';
import RouteGuidanceMiniChat from './RouteGuidanceMiniChat';
import { socket } from '../../admin/chat/socket';

const STATUS_STYLE = {
  pending: 'border-amber-300 bg-amber-100 text-amber-800',
  en_recojo: 'border-purple-300 bg-purple-100 text-purple-800',
  encamino: 'border-blue-400 bg-blue-100 text-blue-800 ring-2 ring-blue-200',
  entregado: 'border-emerald-300 bg-emerald-100 text-emerald-800',
  rechazado: 'border-red-300 bg-red-100 text-red-800',
  cancelado: 'border-slate-300 bg-slate-100 text-slate-600',
  postergado: 'border-orange-300 bg-orange-100 text-orange-800',
};

const STATUS_LABEL = {
  pending: 'Pendiente',
  en_recojo: 'En recojo',
  encamino: 'En camino',
  entregado: 'Entregado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
  postergado: 'Postergado',
};

function formatTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function normalizeSearchValue(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function statusClass(status, isNext) {
  const base = STATUS_STYLE[status] || 'border-slate-300 bg-slate-100 text-slate-700';
  return `${base} ${isNext ? 'shadow-md scale-110' : ''}`;
}

function summaryPill(status, count) {
  if (!count) return null;
  return (
    <span key={status} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLE[status] || STATUS_STYLE.pending}`}>
      {STATUS_LABEL[status] || status}: {count}
    </span>
  );
}

function alertSummaryPill(summary, onClick) {
  const count = summary?.active_count || 0;
  if (!count) return null;
  const highCount = summary?.high_count || 0;
  const openCount = summary?.open_count || 0;
  const tone = highCount
    ? 'border-red-300 bg-red-50 text-red-700'
    : openCount
      ? 'border-amber-300 bg-amber-50 text-amber-700'
      : 'border-blue-200 bg-blue-50 text-blue-700';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black ${tone}`}
      title="Abrir primera entrega con alerta activa"
    >
      <AlertTriangle className="h-3 w-3" />
      Alertas: {count}
      {highCount ? ` · altas ${highCount}` : ''}
    </button>
  );
}

function alertCircleClass(summary) {
  if (!summary?.active_count) return '';
  const hasUnacknowledged = summary.open_count > 0 || summary.has_unacknowledged;
  if (summary.highest_severity === 'high') {
    return hasUnacknowledged
      ? '!border-red-500 ring-4 ring-red-100 fm-alert-blink-high'
      : '!border-red-400 ring-2 ring-red-100';
  }
  if (summary.highest_severity === 'low') {
    return hasUnacknowledged
      ? '!border-slate-500 ring-4 ring-slate-100 fm-alert-blink-low'
      : '!border-slate-400 ring-2 ring-slate-100';
  }
  return hasUnacknowledged
    ? '!border-amber-500 ring-4 ring-amber-100 fm-alert-blink-medium'
    : '!border-amber-400 ring-2 ring-amber-100';
}

function alertBadgeClass(summary) {
  if (summary?.highest_severity === 'high') return 'bg-red-600 text-white';
  if (summary?.highest_severity === 'low') return 'bg-slate-600 text-white';
  return 'bg-amber-500 text-white';
}

function readinessTone(status) {
  if (status === 'ready') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'blocked') return 'border-red-200 bg-red-50 text-red-700';
  if (status === 'attention') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function upcomingReadinessPill(readiness, onClick) {
  if (!readiness?.total) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black ${readinessTone(readiness.status)}`}
      title="Abrir primer punto de los proximos 3 que necesita atencion"
    >
      Próximos 3: {readiness.ready_count || 0}/{readiness.total}
    </button>
  );
}

function needsReviewPill(readiness, onClick) {
  if (!readiness?.total) return null;
  const count = readiness.needs_review_count || 0;
  const tone = count
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : 'border-emerald-200 bg-emerald-50 text-emerald-700';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!count}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black disabled:cursor-default ${tone}`}
      title="Abrir primer punto proximo sin revisar"
    >
      Sin revisar: {count}
    </button>
  );
}

function guidanceBadgeClass(kind, status) {
  if (kind === 'reviewed') return 'bg-blue-50 text-blue-700';
  if (status === 'ok') return 'bg-emerald-50 text-emerald-700';
  if (status === 'bad') return 'bg-red-50 text-red-700';
  return 'bg-amber-50 text-amber-700';
}

function guidanceBadges(stop, alertCount) {
  const state = stop?.guidance_state || {};
  const badges = [];
  if (alertCount > 0) {
    badges.push({
      key: 'alert',
      label: `A${alertCount}`,
      title: 'Alertas activas',
      className: alertBadgeClass(stop.alert_summary || {}),
    });
  }
  if (state.reviewed) {
    badges.push({
      key: 'reviewed',
      label: 'Rev',
      title: 'Entrega revisada por operaciones',
      className: guidanceBadgeClass('reviewed'),
    });
  }
  if (state.customer_contact_status === 'notified' || state.customer_contact_status === 'waiting') {
    badges.push({
      key: 'customer-ok',
      label: 'C✓',
      title: state.customer_contact_status === 'waiting' ? 'Cliente avisado y espera' : 'Cliente avisado',
      className: guidanceBadgeClass('customer', 'ok'),
    });
  } else if (state.reviewed || state.customer_contact_status === 'no_answer') {
    badges.push({
      key: 'customer-pending',
      label: 'C?',
      title: state.customer_contact_status === 'no_answer' ? 'Cliente no contesta' : 'Cliente pendiente',
      className: guidanceBadgeClass('customer', state.customer_contact_status === 'no_answer' ? 'bad' : 'pending'),
    });
  }
  if (state.store_contact_status === 'notified') {
    badges.push({
      key: 'store-ok',
      label: 'T✓',
      title: 'Tienda avisada',
      className: guidanceBadgeClass('store', 'ok'),
    });
  } else if (state.reviewed || state.store_contact_status === 'no_answer') {
    badges.push({
      key: 'store-pending',
      label: 'T?',
      title: state.store_contact_status === 'no_answer' ? 'Tienda no contesta' : 'Tienda pendiente',
      className: guidanceBadgeClass('store', state.store_contact_status === 'no_answer' ? 'bad' : 'pending'),
    });
  }
  return badges;
}

function buildDriverSearchText(driver) {
  const timelineText = (driver.timeline || []).flatMap(stop => [
    stop.delivery_point_id,
    stop.sequence,
    stop.status,
    stop.intent_label,
    stop.recipient_name,
    stop.district,
    stop.address,
    stop.store_name,
    stop.provider_name,
    stop.alert_summary?.types?.join(' '),
    stop.alert_summary?.latest_alert?.message,
    stop.guidance_state?.readiness_status,
    stop.guidance_state?.customer_contact_status,
    stop.guidance_state?.store_contact_status,
  ]);

  return normalizeSearchValue([
    driver.driver_name,
    driver.driver_id,
    driver.phone_number,
    driver.next_delivery_point_id,
    driver.next_sequence,
    ...timelineText,
  ].filter(value => value !== null && value !== undefined && value !== '').join(' '));
}

export default function DriverTimelineDrawer({ open, userId, role = 'admin', onClose, onSelectConversation }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openingDeliveryId, setOpeningDeliveryId] = useState(null);
  const [openingSupportKey, setOpeningSupportKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [activeGuidance, setActiveGuidance] = useState(null);
  const timelineRefs = useRef({});
  const nextStopRefs = useRef({});

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAdminDriverTimeline();
      setData(result);
    } catch (err) {
      console.error('Driver timeline load error:', err);
      setError(err?.response?.data?.error || 'No se pudo cargar la ruta de motorizados.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) setActiveGuidance(null);
  }, [open]);

  useEffect(() => {
    if (!open || !userId) return undefined;
    const payload = { role, user_id: userId };
    const join = () => socket.emit('join_delivery_alerts', payload);
    const refreshFromAlert = () => load();

    join();
    socket.on('delivery_alert_created', refreshFromAlert);
    socket.on('delivery_alert_updated', refreshFromAlert);
    socket.on('driver_timeline_guidance_updated', refreshFromAlert);
    socket.on('connect', join);

    return () => {
      socket.off('delivery_alert_created', refreshFromAlert);
      socket.off('delivery_alert_updated', refreshFromAlert);
      socket.off('driver_timeline_guidance_updated', refreshFromAlert);
      socket.off('connect', join);
      socket.emit('leave_delivery_alerts', payload);
    };
  }, [load, open, role, userId]);

  const drivers = useMemo(() => data?.drivers || [], [data]);
  const normalizedSearchTerm = useMemo(() => normalizeSearchValue(searchTerm.trim()), [searchTerm]);
  const filteredDrivers = useMemo(() => {
    if (!normalizedSearchTerm) return drivers;
    return drivers.filter(driver => buildDriverSearchText(driver).includes(normalizedSearchTerm));
  }, [drivers, normalizedSearchTerm]);

  useEffect(() => {
    if (!open || loading || !filteredDrivers.length || typeof window === 'undefined') return undefined;

    const frame = window.requestAnimationFrame(() => {
      filteredDrivers.forEach(driver => {
        const key = String(driver.driver_id);
        const container = timelineRefs.current[key];
        if (!container) return;

        const hasNextStop = (driver.timeline || []).some(stop => stop.is_next);
        const nextElement = hasNextStop ? nextStopRefs.current[key] : null;
        if (!nextElement) {
          container.scrollLeft = 0;
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const nextRect = nextElement.getBoundingClientRect();
        const centeredLeft = Math.max(
          0,
          container.scrollLeft
            + (nextRect.left - containerRect.left)
            - (container.clientWidth / 2)
            + (nextRect.width / 2)
        );
        container.scrollTo({ left: centeredLeft, behavior: 'auto' });
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [open, loading, filteredDrivers]);

  const isOpening = Boolean(openingDeliveryId || openingSupportKey);

  function guidanceKey(driverId, deliveryPointId) {
    return `${driverId || 'driver'}:${deliveryPointId || 'delivery'}`;
  }

  function openGuidance(driver, stop, initialTargetKey = 'driver') {
    if (!stop?.delivery_point_id) return;
    const key = guidanceKey(driver?.driver_id, stop.delivery_point_id);
    setActiveGuidance(prev => (
      prev?.key === key && prev?.initialTargetKey === initialTargetKey
        ? null
        : {
            key,
            driverId: driver?.driver_id,
            deliveryPointId: stop.delivery_point_id,
            initialTargetKey,
          }
    ));
  }

  function openFirstAlert(driver) {
    const alertedStop = (driver?.timeline || []).find(stop => stop.alert_summary?.active_count > 0);
    if (alertedStop) {
      openGuidance(driver, alertedStop, 'driver');
    }
  }

  function openStopById(driver, deliveryPointId, initialTargetKey = 'driver') {
    const stop = (driver?.timeline || []).find(item => Number(item.delivery_point_id) === Number(deliveryPointId));
    if (stop) {
      openGuidance(driver, stop, initialTargetKey);
      return true;
    }
    return false;
  }

  function openFirstUpcomingNotReady(driver) {
    const readiness = driver?.upcoming_readiness || {};
    const deliveryPointId = (
      readiness.first_not_ready_delivery_point_id
      || readiness.first_needs_review_delivery_point_id
    );
    if (deliveryPointId && openStopById(driver, deliveryPointId, 'driver')) return;
    const nextStop = (driver?.timeline || []).find(stop => stop.is_next);
    if (nextStop) openGuidance(driver, nextStop, 'driver');
  }

  function openFirstUpcomingNeedsReview(driver) {
    const deliveryPointId = driver?.upcoming_readiness?.first_needs_review_delivery_point_id;
    if (deliveryPointId) {
      openStopById(driver, deliveryPointId, 'driver');
    }
  }

  async function openDelivery(deliveryPointId) {
    if (!deliveryPointId || isOpening) return;
    setOpeningDeliveryId(deliveryPointId);
    setError(null);
    try {
      const conversation = await createOrGetDeliveryConversation(deliveryPointId);
      onSelectConversation?.(conversation);
      onClose?.();
    } catch (err) {
      console.error('Open delivery conversation error:', err);
      setError(err?.response?.data?.error || 'No se pudo abrir el chat de esta entrega.');
    } finally {
      setOpeningDeliveryId(null);
    }
  }

  async function openSupport(scope, userId, label) {
    if (!scope || !userId || isOpening) return;
    const supportKey = `${scope}:${userId}`;
    setOpeningSupportKey(supportKey);
    setError(null);
    try {
      const conversation = await createOrGetSupportConversation(scope, userId);
      onSelectConversation?.(conversation);
      onClose?.();
    } catch (err) {
      console.error(`Open ${scope} support conversation error:`, err);
      setError(err?.response?.data?.error || `No se pudo abrir soporte directo${label ? ` para ${label}` : ''}.`);
    } finally {
      setOpeningSupportKey(null);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/40">
      <style>{`
        @keyframes fmAlertBlinkAmber {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.75); }
          50% { box-shadow: 0 0 0 8px rgba(245, 158, 11, 0.12); }
        }
        @keyframes fmAlertBlinkRed {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.8); }
          50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.14); }
        }
        @keyframes fmAlertBlinkSlate {
          0%, 100% { box-shadow: 0 0 0 0 rgba(100, 116, 139, 0.65); }
          50% { box-shadow: 0 0 0 8px rgba(100, 116, 139, 0.12); }
        }
        .fm-alert-blink-medium { animation: fmAlertBlinkAmber 1.1s infinite; }
        .fm-alert-blink-high { animation: fmAlertBlinkRed 0.85s infinite; }
        .fm-alert-blink-low { animation: fmAlertBlinkSlate 1.3s infinite; }
        @media (prefers-reduced-motion: reduce) {
          .fm-alert-blink-medium,
          .fm-alert-blink-high,
          .fm-alert-blink-low { animation: none; }
        }
      `}</style>
      <button
        type="button"
        aria-label="Cerrar timeline de motorizados"
        className="hidden flex-1 sm:block"
        onClick={onClose}
      />
      <aside className="flex h-full w-full max-w-5xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-50 p-2 text-blue-700">
              <Truck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-slate-950 font-montserrat">Motorizados hoy</h2>
              <p className="text-xs text-slate-500">
                Timeline operativo {data?.date ? `- ${data.date}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {error && (
          <div className="m-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500 focus-within:border-blue-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-100">
            <Search className="h-4 w-4" />
            <input
              type="search"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              placeholder="Buscar por motorizado, ID, telefono, distrito, tienda o proveedor..."
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400"
            />
          </div>

          {loading && !drivers.length && (
            <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-8 text-sm font-semibold text-slate-500">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Cargando motorizados...
            </div>
          )}

          {!loading && !drivers.length && !error && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
              <Truck className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm font-bold text-slate-700">No hay motorizados activos para hoy.</p>
              <p className="mt-1 text-xs text-slate-500">Apareceran cuando tengan entregas asignadas no terminales.</p>
            </div>
          )}

          {!loading && drivers.length > 0 && !filteredDrivers.length && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
              <Search className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm font-bold text-slate-700">No hay motorizados que coincidan con la busqueda.</p>
              <p className="mt-1 text-xs text-slate-500">Prueba con nombre, ID, telefono, distrito, tienda o proveedor.</p>
            </div>
          )}

          <div className="space-y-4">
            {filteredDrivers.map(driver => {
              const nextStop = (driver.timeline || []).find(stop => stop.is_next);
              const driverSupportKey = `driver:${driver.driver_id}`;
              const activeStop = (driver.timeline || []).find(stop => (
                activeGuidance?.driverId === driver.driver_id
                && activeGuidance?.deliveryPointId === stop.delivery_point_id
              ));
              return (
                <section key={driver.driver_id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <button
                        type="button"
                        onClick={() => openSupport('driver', driver.driver_id, driver.driver_name)}
                        disabled={!driver.driver_id || isOpening}
                        className="group -m-1 flex items-center gap-2 rounded-xl p-1 text-left transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                        title="Abrir soporte directo con el motorizado"
                      >
                        <span className="rounded-lg bg-blue-50 p-1.5 text-blue-700 transition group-hover:bg-blue-100">
                          <Truck className="h-4 w-4" />
                        </span>
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-extrabold text-slate-950 group-hover:text-blue-700">
                              {driver.driver_name}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                              ID {driver.driver_id}
                            </span>
                            {openingSupportKey === driverSupportKey && (
                              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-black text-blue-700">
                                Abriendo...
                              </span>
                            )}
                          </span>
                        </span>
                      </button>
                      <p className="mt-1 text-xs text-slate-500">
                        Siguiente: {nextStop ? `#${nextStop.delivery_point_id} - Seq ${nextStop.sequence || '-'}` : 'sin siguiente'}
                        {nextStop?.district ? ` - ${nextStop.district}` : ''}
                      </p>
                      {driver.phone_number && (
                        <p className="mt-0.5 text-[11px] font-semibold text-slate-400">Tel: {driver.phone_number}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openDelivery(driver.next_delivery_point_id)}
                      disabled={!driver.next_delivery_point_id || isOpening}
                      className="rounded-lg bg-[#B1336E] px-3 py-2 text-xs font-extrabold text-white hover:bg-[#922a5b] disabled:opacity-50"
                    >
                      {openingDeliveryId === driver.next_delivery_point_id ? 'Abriendo...' : 'Abrir siguiente chat'}
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {['encamino', 'pending', 'en_recojo', 'postergado', 'entregado', 'rechazado', 'cancelado']
                      .map(status => summaryPill(status, driver.summary?.[status]))}
                    {alertSummaryPill(driver.alerts_summary, () => openFirstAlert(driver))}
                    {upcomingReadinessPill(driver.upcoming_readiness, () => openFirstUpcomingNotReady(driver))}
                    {needsReviewPill(driver.upcoming_readiness, () => openFirstUpcomingNeedsReview(driver))}
                  </div>

                  <div
                    ref={element => {
                      const key = String(driver.driver_id);
                      if (element) timelineRefs.current[key] = element;
                      else delete timelineRefs.current[key];
                    }}
                    className="mt-4 overflow-x-auto px-1 pb-3"
                  >
                    <div className="relative flex min-w-max items-start gap-4">
                      <div className="absolute left-7 right-7 top-7 h-0.5 bg-slate-200" />
                      {(driver.timeline || []).map(stop => {
                        const storeSupportKey = `store:${stop.store_target?.user_id || ''}`;
                        const providerSupportKey = `provider:${stop.provider_target?.user_id || ''}`;
                        const stopGuidanceKey = guidanceKey(driver.driver_id, stop.delivery_point_id);
                        const isGuidanceActive = activeGuidance?.key === stopGuidanceKey;
                        const stopAlertSummary = stop.alert_summary || {};
                        const activeAlertCount = stopAlertSummary.active_count || 0;
                        const stopBadges = guidanceBadges(stop, activeAlertCount);
                        return (
                          <div key={stop.delivery_point_id} className="relative z-10 flex w-36 shrink-0 flex-col items-center text-center">
                            <button
                              ref={stop.is_next ? element => {
                                const key = String(driver.driver_id);
                                if (element) nextStopRefs.current[key] = element;
                                else delete nextStopRefs.current[key];
                              } : undefined}
                              type="button"
                              onClick={() => openGuidance(driver, stop, 'driver')}
                              disabled={isOpening}
                              className={`flex min-h-[172px] w-full flex-col items-center overflow-visible rounded-2xl px-2 py-2 text-center transition disabled:opacity-60 ${
                                isGuidanceActive ? 'bg-blue-50 ring-2 ring-blue-300' : 'hover:bg-slate-50'
                              }`}
                              title={`Entrega #${stop.delivery_point_id} - ${STATUS_LABEL[stop.status] || stop.status}`}
                            >
                              <span className={`relative flex h-14 w-14 items-center justify-center rounded-full border text-base font-black transition ${statusClass(stop.status, stop.is_next)} ${alertCircleClass(stopAlertSummary)}`}>
                                {stop.sequence || '-'}
                              </span>
                              <span className="mt-1 max-w-[128px] truncate text-[10px] font-bold text-slate-700">
                                #{stop.delivery_point_id}
                              </span>
                              <span className="max-w-[128px] truncate text-[10px] text-slate-500">
                                {stop.district || 'Sin distrito'}
                              </span>
                              <span className="max-w-[128px] truncate text-[10px] font-semibold text-slate-400">
                                {STATUS_LABEL[stop.status] || stop.intent_label || stop.status}
                              </span>
                              {formatTime(stop.arrival_time) && (
                                <span className="text-[10px] text-slate-400">{formatTime(stop.arrival_time)}</span>
                              )}
                              {stop.is_next && (
                                <span className="mt-1 rounded-full bg-blue-600 px-1.5 py-0.5 text-[9px] font-black uppercase text-white">
                                  Next
                                </span>
                              )}
                              {stopBadges.length > 0 && (
                                <span className="mt-1 flex min-h-[19px] max-w-[128px] flex-wrap justify-center gap-1">
                                  {stopBadges.map(badge => (
                                    <span
                                      key={badge.key}
                                      title={badge.title}
                                      className={`rounded-full px-1.5 py-0.5 text-[9px] font-black leading-none ${badge.className}`}
                                    >
                                      {badge.label}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </button>

                            <div className="mt-1 flex min-h-[20px] max-w-[136px] flex-wrap justify-center gap-1">
                              <button
                                type="button"
                                onClick={event => {
                                  event.stopPropagation();
                                  openGuidance(driver, stop, 'driver');
                                }}
                                disabled={isOpening}
                                className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-black text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                                title="Guiar al motorizado"
                              >
                                Drv
                              </button>
                              <button
                                type="button"
                                onClick={event => {
                                  event.stopPropagation();
                                  openGuidance(driver, stop, 'final_customer');
                                }}
                                disabled={isOpening}
                                className={`rounded-full px-1.5 py-0.5 text-[9px] font-black disabled:opacity-50 ${
                                  stop.customer_channel_status?.whatsapp_available
                                    ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                                title={stop.customer_channel_status?.whatsapp_disabled_reason || 'Contactar cliente'}
                              >
                                {stop.customer_channel_status?.whatsapp_available ? 'WA' : 'App'}
                              </button>
                              {stop.store_target?.user_id && (
                                <button
                                  type="button"
                                  onClick={event => {
                                    event.stopPropagation();
                                    openGuidance(driver, stop, 'store');
                                  }}
                                  disabled={isOpening}
                                  className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-black text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                                  title={`Avisar a ${stop.store_target?.label || stop.store_name || `tienda ${stop.store_target.user_id}`}`}
                                >
                                  {openingSupportKey === storeSupportKey ? '...' : 'Tienda'}
                                </button>
                              )}
                              {stop.provider_target?.user_id && (
                                <button
                                  type="button"
                                  onClick={event => {
                                    event.stopPropagation();
                                    openGuidance(driver, stop, 'provider');
                                  }}
                                  disabled={isOpening}
                                  className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-black text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                                  title={`Avisar a ${stop.provider_target?.label || stop.provider_name || `proveedor ${stop.provider_target.user_id}`}`}
                                >
                                  {openingSupportKey === providerSupportKey ? '...' : 'Proveedor'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {activeStop && (
                    <RouteGuidanceMiniChat
                      stop={activeStop}
                      driver={driver}
                      initialTargetKey={activeGuidance?.initialTargetKey}
                      onOpenFullDelivery={openDelivery}
                      onOpenFullSupport={openSupport}
                      onAlertUpdated={load}
                      onGuidanceUpdated={load}
                      isOpening={isOpening}
                    />
                  )}
                </section>
              );
            })}
          </div>
        </div>
      </aside>
    </div>
  );
}
